import { Modal, SubmitButton } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import {
    getAddOns,
    getExperiencePurchaseItems,
    handleSuccessfulSubmission,
    isNonCanceledExperienceItem,
    isPast,
    PurchaseActionOption,
    PurchasePriceStrategy,
} from "common";
import { isPackageProduct } from "common/src/models/Product";
import { find, isEmpty } from "lodash";
import React, { useMemo, useState } from "react";
import { useMutation } from "react-query";
import {
    AddOn,
    Experience,
    ExperiencePurchaseItem,
    LineItem,
    LineItemType,
    Product,
    Purchase,
    PurchaseItem,
} from "types";
import { api } from "../../api/api";
import { useResource } from "../../hooks/useResource";
import { useVisibility } from "../../hooks/useVisibility";
import { getPackageAddOnsInExperience } from "../../models/Package";
import { getFirstAvailableExperiencePurchaseItem, isPriceConfigurationUpdated } from "../../models/PurchaseItem";
import { ConfirmLeaveModal } from "../Common/ConfirmLeaveModal";
import { NotifyCustomerCheckbox } from "../Common/NotifyCustomerCheckbox";
import { SecondaryButton } from "../Common/SecondaryButton";
import { RefundOption } from "../Dashboard/RefundOption";
import { Loader } from "../Loader";
import { AddOnLimits, AddOnLimitsProvider } from "../PurchaseCreate/AddOnLimitsContext";
import { CreatePurchaseAddOnList } from "../PurchaseCreate/CreatePurchaseAddOnList";
import { PaymentBreakdown, PriceStrategy } from "./PaymentBreakdown";
import { PurchaseItemSelect } from "./PurchaseItemSelect";
import { PurchaseItemSummaryTile } from "./PurchaseItemSummaryTile";
import { useAddOnRestrictionErrors } from "@/hooks/useAddOnRestrictionErrors";
import { shouldRetainPriceByDefault } from "@/models/Seller";

// This type is for the updated lineItems for this Modal.
type EditedLineItem = Partial<LineItem> & {
    template: { id: ID };
    quantity: number | null;
    type: LineItemType;
};

interface EditPurchaseAddOnsModalProps {
    isOpen: boolean;
    onClose: (
        nextModalOption: PurchaseActionOption | null,
        nextRefundOption: RefundOption | null,
        updatedPurchase?: Purchase,
        refundedItem?: PurchaseItem,
    ) => void;
    purchase: Purchase;
    item: ExperiencePurchaseItem;
}

export const EditPurchaseAddOnsModal = ({ onClose, purchase, item, isOpen }: EditPurchaseAddOnsModalProps) => {
    return (
        // Passing as onClose={onClose} will pass JS event as 1st param which is not expected,
        // and will cause the JS event to be set as `modalOption`.
        <Modal size="huge" isOpen={isOpen} onClose={() => onClose(null, null)}>
            <ModalContent purchase={purchase} item={item} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ onClose, purchase, item }: Omit<EditPurchaseAddOnsModalProps, "isOpen">) => {
    const [notifyCustomer, setNotifyCustomer] = useState<boolean>(true);
    const [dirty, setDirty] = useState(false);
    const [selectedItem, setSelectedItem] = useState<ExperiencePurchaseItem>(
        getFirstAvailableExperiencePurchaseItem(purchase, item),
    );

    const addonLineItems = selectedItem.lineItems.filter((lineItem) => {
        return lineItem.template?.id && [LineItemType.ADDON, LineItemType.PICKUP].includes(lineItem.type);
    });

    // This state variable can have only type, template and quantity. so,type is partial<LineItem>
    const [lineItems, setLineItems] = useState<EditedLineItem[]>([]);
    const [retainPrice, setRetainPrice] = useState(!shouldRetainPriceByDefault());
    const [showRetainPriceToggle, setShowRetainPriceToggle] = useState(false);
    const [nextItems, setNextItems] = useState<{
        item: ExperiencePurchaseItem | null;
        lineItems: EditedLineItem[];
    } | null>(null);
    const [isPromptVisible, { show: showPrompt, hide: hidePrompt }] = useVisibility(false);

    const { data: experience, isLoading: isExperienceLoading } = useResource<Experience>([
        "products",
        selectedItem.product.id,
    ]);

    const addOns = useMemo(() => (experience ? (getAddOns(experience) as AddOn[]) : []), [experience]);
    // Only the API can check the event limit. The dry run has the edited quantities, so the error shows while editing.
    const addOnRestrictions = useAddOnRestrictionErrors(addOns);
    const { restrictionErrors, overrideRestrictions } = addOnRestrictions;
    const { handleError, handleOverrideChange, clear, reset } = addOnRestrictions;

    const isPastDated = isPast(selectedItem.arrivalDateTime);
    const addOnLimits = useMemo<AddOnLimits>(() => {
        return {
            isPastDated,
            shouldAllowSavedQuantity: true,
            guestCount: selectedItem.quantity,
            errors: restrictionErrors,
            overrideRestrictions,
            onOverrideRestrictionsChange: handleOverrideChange,
        };
    }, [isPastDated, selectedItem.quantity, restrictionErrors, overrideRestrictions, handleOverrideChange]);

    const parentProductId = item?.parentProduct?.id;
    const { data: parentProduct } = useResource<Product>(["products", parentProductId], {
        isEnabled: !!parentProductId,
    });
    const isPackage = isPackageProduct(parentProduct);

    const {
        mutateAsync: applyChangeAddons,
        isLoading,
        isSuccess,
    } = useMutation(
        async ({
            purchase,
            item,
            lineItems,
        }: {
            purchase: Purchase;
            item: ExperiencePurchaseItem;
            lineItems: EditedLineItem[];
        }) => {
            const payload = getPayload({ item, lineItems, retainPrice, showRetainPriceToggle, overrideRestrictions });
            const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}`, {
                ...payload,
                _emailOverrides: { sendEmailToTraveler: notifyCustomer },
            });
            return data;
        },
    );

    const handleLineItemUpdate = useMemoizedFn((quantity: number, id?: ID) => {
        if (!id) {
            return;
        }

        const item = find(lineItems, { template: { id } });
        if (item) {
            const newLineItems = lineItems.map((lineItem) => {
                if (lineItem?.template.id === id) {
                    return { ...lineItem, quantity };
                }

                return lineItem;
            });
            setLineItems(newLineItems);
        } else {
            const newItem = { template: { id }, quantity, type: LineItemType.ADDON };
            const updatedLineItems = [...lineItems, newItem];
            setLineItems(updatedLineItems);
        }

        setDirty(true);
    });

    const handleChoiceLineItemUpdate = useMemoizedFn((templatesIds: string[], id: ID, shouldDecreaseOnly: boolean) => {
        // For choice addons we need to check if any of the choice exists in purchase items as well,
        // otherwise it will create duplicates
        // For eg, if choiceAddon has a,b,c and while creating if we selected a and then if
        // we try to modify to b or c, then we should pass quantity of a as 0, and quantity of newly
        // selected item as 1.

        let hasOriginalLineItemModified = false;
        const originalPurchaseItemLineItems = addonLineItems.filter((lineItem: LineItem) => {
            return templatesIds.includes(lineItem.template.id);
        });

        const newLineItems = lineItems.map((lineItem) => {
            if (
                originalPurchaseItemLineItems.some(
                    (originalLineItem) => originalLineItem.template.id === lineItem.template.id,
                )
            ) {
                hasOriginalLineItemModified = true;
            }

            if (templatesIds.includes(lineItem.template.id)) {
                return { ...lineItem, quantity: 0 };
            }

            return lineItem;
        });

        // In case we haven't zeroed out the original lineItem, we will do it
        // also, adding to the list only if it is not already exist.
        if (originalPurchaseItemLineItems.length > 0 && !hasOriginalLineItemModified) {
            for (const originalPurchaseItemLineItem of originalPurchaseItemLineItems) {
                if (shouldDecreaseOnly) {
                    newLineItems.push({ ...originalPurchaseItemLineItem, quantity: 0, includeInAmount: false });
                } else {
                    newLineItems.push({ ...originalPurchaseItemLineItem, quantity: 0 });
                }
            }
        }

        setDirty(true);
        if (id === "None") {
            setLineItems([...newLineItems]);
            return;
        }

        const lineItemIndex = newLineItems.findIndex((lineItem) => lineItem.template.id === id);

        if (lineItemIndex >= 0) {
            newLineItems[lineItemIndex].quantity = 1;
        } else {
            newLineItems.push({ template: { id }, quantity: 1, type: LineItemType.ADDON });
        }

        setLineItems([...newLineItems]);
    });

    const updatedPurchase = useMemo(() => {
        let item;

        if (!isEmpty(lineItems)) {
            item = {
                id: selectedItem.id,
                lineItems: lineItems.map((lineItem) => ({
                    template: { id: lineItem?.template?.id },
                    quantity: lineItem.quantity,
                    includeInAmount: lineItem.includeInAmount,
                    ...(overrideRestrictions[lineItem.template?.id ?? ""] && { _overrideRestrictions: true }),
                })),
            };
            return retainPrice ? { items: [{ ...item, _priceStrategy: "retain" }] } : { items: [item] };
        }
    }, [lineItems, selectedItem, retainPrice, overrideRestrictions]);

    if (isExperienceLoading || !experience) {
        return <Loader />;
    }

    const addOnCatalogItems = isPackage ? getPackageAddOnsInExperience(parentProduct, experience) : undefined;

    const handleSave = async () => {
        try {
            const updatedPurchase = await applyChangeAddons({ purchase, item: selectedItem, lineItems });
            clear();
            handleSuccessfulSubmission(() => {
                const purchaseActionOption = updatedPurchase.balance > 0 ? PurchaseActionOption.COLLECT_BALANCE : null;
                const refundOption = updatedPurchase.balance < 0 ? RefundOption.RETURN_PAYMENT : null;
                onClose(purchaseActionOption, refundOption, updatedPurchase, selectedItem);
            });
        } catch (error) {
            if (handleError(error)) {
                return;
            }
            throw error;
        }
    };

    const handlePaymentBreakdownUpdate = (purchase: Purchase | null) => {
        clear();

        if (isPriceConfigurationUpdated(purchase, item) && !showRetainPriceToggle) {
            setShowRetainPriceToggle(true);
            setRetainPrice(!retainPrice);
        }
    };

    const handleItemChange = (item: PurchaseItem) => {
        if (dirty) {
            setNextItems({ item: item as ExperiencePurchaseItem, lineItems: item.lineItems });
            showPrompt();
            return;
        }

        reset();
        setSelectedItem(item as ExperiencePurchaseItem);
        setLineItems(item.lineItems);
    };

    const handleConfirmLeave = () => {
        setNextItems(null);
        hidePrompt();
        setDirty(false);

        if (nextItems?.item && nextItems?.lineItems) {
            reset();
            setSelectedItem(nextItems.item);
            setLineItems(nextItems.lineItems);
        }
    };

    const handleCancelLeave = () => {
        setNextItems(null);
        hidePrompt();
    };

    return (
        <fieldset disabled={isExperienceLoading}>
            <ConfirmLeaveModal
                disableRouterPrompt
                copy="You have unsaved changes on this screen. Navigating away will cause you to lose these changes. Are you sure you want to continue?"
                manualShowPrompt={isPromptVisible}
                handleConfirm={handleConfirmLeave}
                handleCancel={handleCancelLeave}
            />
            <Modal.Header>Change Add-Ons</Modal.Header>
            <Modal.Body className="flex-col space-y-4">
                {purchase.items.length > 1 ? (
                    <PurchaseItemSelect
                        // Omit gifts and store credit since we can't change addons for it.
                        items={getExperiencePurchaseItems(purchase)}
                        value={selectedItem}
                        className="flex-1"
                        onChange={handleItemChange}
                    />
                ) : null}
                <div className="flex space-x-4">
                    <div className="flex-1">
                        <PurchaseItemSummaryTile purchase={purchase} item={selectedItem} />
                        <PurchasePriceStrategy
                            isChecked={retainPrice}
                            isVisible={showRetainPriceToggle}
                            onChange={() => setRetainPrice(!retainPrice)}
                        />
                        {isNonCanceledExperienceItem(selectedItem) && (
                            <AddOnLimitsProvider value={addOnLimits}>
                                <CreatePurchaseAddOnList
                                    shouldShowPurchaseAddons
                                    experience={experience}
                                    catalogItems={addOnCatalogItems}
                                    purchaseId={purchase?.id}
                                    lineItems={addonLineItems}
                                    onLineItemUpdate={handleLineItemUpdate}
                                    onChoiceLineItemUpdate={handleChoiceLineItemUpdate}
                                />
                            </AddOnLimitsProvider>
                        )}
                    </div>
                    <div className="flex-1">
                        <div className="bg-gray-lighter p-6">
                            <PaymentBreakdown
                                isEditable={false}
                                purchase={{ ...purchase }}
                                item={selectedItem}
                                payload={updatedPurchase}
                                onUpdate={handlePaymentBreakdownUpdate}
                                onAddOnLimitError={handleError}
                            />
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="flex justify-end">
                <NotifyCustomerCheckbox
                    isChecked={notifyCustomer}
                    onChange={(e: any) => setNotifyCustomer(e.target.checked)}
                />

                {/**
                 * Passing as onClick={onClose} will pass JS event as 1st param which is not expected,
                 * and will cause the JS event to be set as `modalOption`.
                 */}
                <SecondaryButton onClick={() => onClose(null, null)}>Cancel</SecondaryButton>
                <SubmitButton disabled={!dirty} isLoading={isLoading} isSuccess={isSuccess} onClick={handleSave}>
                    Save Changes
                </SubmitButton>
            </Modal.Footer>
        </fieldset>
    );
};

interface AddOnsPayloadParams {
    item: ExperiencePurchaseItem;
    lineItems: EditedLineItem[];
    retainPrice: boolean;
    showRetainPriceToggle: boolean;
    overrideRestrictions: Record<ID, boolean>;
}

const getPayload = ({
    item,
    lineItems,
    retainPrice,
    showRetainPriceToggle,
    overrideRestrictions,
}: AddOnsPayloadParams) => {
    const addonLineItems = lineItems.filter((lineItem) =>
        [LineItemType.ADDON, LineItemType.PICKUP].includes(lineItem.type),
    );

    const lineItemsInPayload = addonLineItems.map((lineItem) => {
        return {
            template: { id: lineItem.template.id },
            quantity: lineItem.quantity ?? 0,
            includeInAmount: lineItem.includeInAmount,
            ...(overrideRestrictions[lineItem.template.id] && { _overrideRestrictions: true }),
        };
    });

    if (showRetainPriceToggle && retainPrice) {
        return { items: [{ id: item.id, lineItems: lineItemsInPayload, _priceStrategy: PriceStrategy.RETAIN }] };
    }

    return { items: [{ id: item.id, lineItems: lineItemsInPayload }] };
};
